import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { FaInstagram, FaLinkedinIn, FaTiktok, FaXTwitter, FaYoutube } from 'react-icons/fa6';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useTranslation } from 'react-i18next';
import { getLocalizedRouteLanguage } from '../utils/localizedRoutes';
import { useLanguageLearning } from '../contexts/LanguageLearningContext';
import { buildPublicLearningPath, buildPublicPath, getLinkLearningLanguage } from '../lib/learningRoutes';
import AppStoreBadge from './AppStoreBadge';
import GooglePlayBadge from './GooglePlayBadge';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { preferredLanguage } = useLanguageLearning();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const currentUiLanguage = getLocalizedRouteLanguage(
    location.pathname,
    i18n.resolvedLanguage || i18n.language
  );
  // Chosen learning language, or the page language without a preference.
  const learningLanguage = getLinkLearningLanguage(preferredLanguage, location.pathname);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setMessage(t('common:footer.newsletter.emailRequired'));
      setMessageType('error');
      return;
    }

    if (!validateEmail(email)) {
      setMessage(t('common:footer.newsletter.emailInvalid'));
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const submitNewsletterSubscription = httpsCallable(functions, 'submitNewsletterSubscription');
      await submitNewsletterSubscription({
        email: email.trim().toLowerCase(),
      });

      setMessage(t('common:footer.newsletter.subscribeSuccess'));
      setMessageType('success');
      setEmail('');
    } catch (error) {
      console.error('Error adding subscriber:', error);
      setMessage(t('common:footer.newsletter.subscribeError'));
      setMessageType('error');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 5000);
    }
  };

  const essentialLinks = [
    { href: '/learning-guide', text: t('common:footer.links.learningGuide'), routeType: 'learning' },
    { href: '/library', text: t('common:navigation.library'), routeType: 'learning' },
    { href: '/store', text: t('common:navigation.store'), routeType: 'ui' },
    { href: '/blog', text: t('common:footer.links.blog'), routeType: 'ui' },
    { href: '/faq', text: t('common:footer.links.faq'), routeType: 'ui' },
    { href: '/contact', text: t('common:footer.links.contactUs'), routeType: 'ui' },
  ];

  const legalLinks = [
    { href: '/privacy', text: t('common:footer.links.privacyPolicy') },
    { href: '/terms', text: t('common:footer.links.termsOfService') },
    { href: '/cookies', text: t('common:footer.links.cookiePolicy') },
  ];

  const elevenLabsGrant = {
    href: 'https://elevenlabs.io/startup-grants',
    imageSrc: 'https://eleven-public-cdn.elevenlabs.io/payloadcms/pwsc4vchsqt-ElevenLabsGrants.webp',
    alt: 'ElevenLabs Grants',
  };

  const socialLinks = [
    {
      href: 'https://www.instagram.com/join_aprendos/',
      label: 'Aprendos on Instagram',
      Icon: FaInstagram,
    },
    {
      href: 'https://www.tiktok.com/@join_aprendos',
      label: 'Aprendos on TikTok',
      Icon: FaTiktok,
    },
    {
      href: 'https://x.com/aprendos_com',
      label: 'Aprendos on X',
      Icon: FaXTwitter,
    },
    {
      href: 'https://no.linkedin.com/company/aprendos',
      label: 'Aprendos on LinkedIn',
      Icon: FaLinkedinIn,
    },
  ];

  const youtubeChannels = [
    { href: 'https://www.youtube.com/channel/UCPFpyM3O8EQ6KvXe-sqKx-A', label: 'English' },
    { href: 'https://www.youtube.com/channel/UC0omH6jQ-a0gAlAmc3G_wAg', label: 'Español' },
    { href: 'https://www.youtube.com/channel/UC-saN88EgG7ZUBE7PRclJIw', label: 'Norsk' },
    { href: 'https://www.youtube.com/channel/UCtQuUpN0st8t_Sr7bclt3PQ', label: 'Aprendos Language Learning' },
  ];

  // English public pages link to their unprefixed canonical URLs (/faq, not /en/faq).
  const localizeHref = (href) => buildPublicPath(href, currentUiLanguage);
  const resolveHref = (href, routeType) => (
    routeType === 'learning'
      ? buildPublicLearningPath(href, learningLanguage)
      : localizeHref(href)
  );

  return (
    <footer
      className="relative border-t border-stone-200"
      style={{
        background: 'linear-gradient(to bottom, #FDFBF7 0%, #FBF8F3 100%)',
      }}
    >
      <style>{`
        .footer-link {
          color: #57534e;
          transition: color 0.2s ease;
        }
        .footer-link:hover {
          color: #D63AB4;
        }
        .footer-legal-link {
          color: #78716c;
          transition: color 0.2s ease;
        }
        .footer-legal-link:hover {
          color: #D63AB4;
        }
        .footer-social {
          background-color: #F5F5F7;
          color: #57534e;
          transition: background-color 0.2s ease, color 0.2s ease;
        }
        .footer-social:hover {
          background-color: #D63AB4;
          color: #FFFFFF;
        }
      `}</style>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* Desktop: 2-column editorial layout. Mobile: stacked. */}
        <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-16 mb-12">

          {/* Brand + newsletter */}
          <div>
            <Link to={localizeHref('/')} className="inline-flex items-center mb-6">
              <span
                className="text-stone-900 font-bold"
                style={{
                  fontSize: 'clamp(2.4rem, 3.5vw, 3.15rem)',
                  fontFamily: 'var(--font-display)',
                  letterSpacing: '-0.05em',
                  lineHeight: 1,
                }}
              >
                Aprendos
              </span>
            </Link>

            <p className="text-stone-600 leading-relaxed mb-8" style={{ maxWidth: '52ch' }}>
              {t('common:footer.description')}
            </p>

            <div className="mb-8 flex flex-wrap items-center gap-3">
              <AppStoreBadge />
              <GooglePlayBadge />
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mb-3">
                {t('common:footer.sections.stayUpdated')}
              </p>
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md">
                <label className="sr-only" htmlFor="footer-newsletter-email">
                  {t('common:footer.newsletter.ariaLabel', 'Email address for newsletter subscription')}
                </label>
                <input
                  id="footer-newsletter-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('common:footer.newsletter.placeholder')}
                  aria-label={t('common:footer.newsletter.ariaLabel', 'Email address for newsletter subscription')}
                  className="flex-1 px-4 py-3 bg-white border border-stone-200 rounded-full text-sm text-stone-900 placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-[#D63AB4]/40 focus:border-[#D63AB4] transition-all"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading}
                  aria-label={t('common:footer.newsletter.subscribeAriaLabel', 'Subscribe to newsletter')}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#1c1917] text-white text-sm font-semibold hover:-translate-y-0.5 active:translate-y-0 transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ boxShadow: '0 12px 30px rgba(28,25,23,0.12)' }}
                >
                  <Mail size={16} />
                  <span>{loading ? t('common:buttons.subscribing') : t('common:buttons.subscribe')}</span>
                </button>
              </form>
              {message && (
                <div
                  role="status"
                  aria-live="polite"
                  className="mt-3 text-sm px-4 py-2 rounded-full inline-block"
                  style={{
                    backgroundColor: messageType === 'success' ? '#D1FAE5' : '#FEE2E2',
                    color: messageType === 'success' ? '#065F46' : '#991B1B',
                  }}
                >
                  {message}
                </div>
              )}
            </div>
          </div>

          {/* Links column */}
          <nav aria-label={t('common:footer.sections.resources')}>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mb-4">
              {t('common:footer.sections.resources')}
            </p>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
              {essentialLinks.map((link) => (
                <li key={link.text}>
                  <Link to={resolveHref(link.href, link.routeType)} className="footer-link text-sm">
                    {link.text}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mb-4">
                {t('common:footer.sections.follow', 'Follow')}
              </p>
              <div className="flex gap-3">
                {socialLinks.map(({ href, label, Icon }) => (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="footer-social w-10 h-10 rounded-full flex items-center justify-center"
                  >
                    <Icon size={18} />
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-8 border-t border-stone-200 pt-6">
              <p className="text-sm font-semibold text-stone-900 mb-3">Aprendos on YouTube</p>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                {youtubeChannels.map(({ href, label }) => (
                  <a
                    key={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-stone-700 transition-colors hover:text-[#9d1c7f]"
                    aria-label={`${label.startsWith('Aprendos ') ? label : `Aprendos ${label}`} on YouTube`}
                  >
                    <FaYoutube size={20} color="#FF0000" aria-hidden="true" />
                    <span>{label}</span>
                  </a>
                ))}
              </div>
            </div>
          </nav>
        </div>

        {/* ElevenLabs grant credit */}
        <div
          className="mb-10 rounded-3xl border border-stone-200 bg-white/70 px-6 py-5 sm:px-8"
        >
          <div className="flex flex-col items-center justify-between gap-4 text-center lg:flex-row lg:text-left">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">
                {t('common:footer.partnerLabel', 'Voice technology partner')}
              </p>
              <p className="mt-2 text-sm text-stone-600">
                {t('common:footer.partnerDescription', 'Supported through the ElevenLabs Grants program.')}
              </p>
            </div>
            <a
              href={elevenLabsGrant.href}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform duration-200 hover:-translate-y-0.5"
              aria-label="Visit ElevenLabs Grants"
            >
              <img
                src={elevenLabsGrant.imageSrc}
                alt={elevenLabsGrant.alt}
                width="220"
                height="44"
                className="h-auto w-[180px] sm:w-[220px]"
                loading="lazy"
              />
            </a>
          </div>
        </div>

        {/* Bottom bar: copyright + legal */}
        <div className="pt-8 border-t border-stone-200">
          <div className="flex flex-col items-center gap-4 text-center lg:flex-row lg:justify-between lg:text-left">
            <p className="text-sm text-stone-500">
              {t('common:footer.copyright', { year: new Date().getFullYear() })}
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              {legalLinks.map((link) => (
                <Link
                  key={link.text}
                  to={localizeHref(link.href)}
                  className="footer-legal-link text-sm"
                >
                  {link.text}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
